import { Athlete, Session, TestResult, TestType } from '../types';

export const testTypes: TestType[] = [
  'Sprint',
  'Vertical Jump',
  'Horizontal Jump',
  'Bench Press (kg)',
  'Squat Max (kg)',
  'Deadlift (kg)',
  'VO2 Max (ml/kg/min)',
  'Cooper Test (m)',
  'Plank Hold (s)',
  'Agility Drill (s)',
  'Sit & Reach (cm)',
  'Push-ups (1 min) (reps)',
  'Pull-ups (reps)',
];

export const athletes: Athlete[] = [
  { id: '1', name: 'João Silva', position: 'Forward', number: '10' },
  { id: '2', name: 'Maria Santos', position: 'Midfielder', number: '8' },
  { id: '3', name: 'Pedro Costa', position: 'Defender', number: '4' },
  { id: '4', name: 'Ana Oliveira', position: 'Forward', number: '9' },
  { id: '5', name: 'Carlos Pereira', position: 'Goalkeeper', number: '1' },
  { id: '6', name: 'Sofia Rodrigues', position: 'Midfielder', number: '6' },
  { id: '7', name: 'Miguel Ferreira', position: 'Defender', number: '3' },
  { id: '8', name: 'Beatriz Almeida', position: 'Forward', number: '11' },
];

// These sessions are generated dynamically in the pages, not used here
export const sessions: Session[] = [];

// Helper function to generate dates going backwards from yesterday
function generatePastDates(startDate: Date, numWeeks: number): string[] {
  const dates: string[] = [];
  const today = new Date(startDate);

  for (let week = 0; week < numWeeks; week++) {
    for (let day = 0; day < 7; day++) {
      const date = new Date(today);
      date.setDate(today.getDate() - (week * 7 + day));
      dates.push(date.toISOString().split('T')[0]);
    }
  }

  return dates;
}

// Generate realistic test values with slight variations
function generateTestValue(testType: TestType, baseValue: number, variation: number = 0.1, seed: number = 0): number {
  // Use a seeded random for consistency
  const random = Math.sin(seed) * 10000;
  const randomValue = random - Math.floor(random);
  const variance = baseValue * variation * (randomValue - 0.5) * 2;
  return parseFloat((baseValue + variance).toFixed(2));
}

// Yesterday is Feb 14, 2026
const yesterday = new Date('2026-02-14');
const pastDates = generatePastDates(yesterday, 12); // 12 weeks of data (to cover past and future weeks)

// Base values for each athlete and test type
const athleteBaseValues: Record<string, Record<TestType, number>> = {
  '1': { // João Silva
    'Sprint': 11.0,
    'Vertical Jump': 60,
    'Horizontal Jump': 200,
    'Bench Press (kg)': 75,
    'Squat Max (kg)': 120,
    'Deadlift (kg)': 140,
    'VO2 Max (ml/kg/min)': 54,
    'Cooper Test (m)': 2700,
    'Plank Hold (s)': 90,
    'Agility Drill (s)': 14.5,
    'Sit & Reach (cm)': 22,
    'Push-ups (1 min) (reps)': 45,
    'Pull-ups (reps)': 14,
  },
  '2': { // Maria Santos
    'Sprint': 12.3,
    'Vertical Jump': 52,
    'Horizontal Jump': 180,
    'Bench Press (kg)': 50,
    'Squat Max (kg)': 85,
    'Deadlift (kg)': 95,
    'VO2 Max (ml/kg/min)': 50,
    'Cooper Test (m)': 2500,
    'Plank Hold (s)': 85,
    'Agility Drill (s)': 15.8,
    'Sit & Reach (cm)': 28,
    'Push-ups (1 min) (reps)': 38,
    'Pull-ups (reps)': 8,
  },
  '3': { // Pedro Costa
    'Sprint': 13.1,
    'Vertical Jump': 48,
    'Horizontal Jump': 175,
    'Bench Press (kg)': 90,
    'Squat Max (kg)': 140,
    'Deadlift (kg)': 160,
    'VO2 Max (ml/kg/min)': 48,
    'Cooper Test (m)': 2400,
    'Plank Hold (s)': 110,
    'Agility Drill (s)': 16.2,
    'Sit & Reach (cm)': 18,
    'Push-ups (1 min) (reps)': 42,
    'Pull-ups (reps)': 10,
  },
  '4': { // Ana Oliveira
    'Sprint': 11.6,
    'Vertical Jump': 56,
    'Horizontal Jump': 188,
    'Bench Press (kg)': 55,
    'Squat Max (kg)': 95,
    'Deadlift (kg)': 105,
    'VO2 Max (ml/kg/min)': 52,
    'Cooper Test (m)': 2600,
    'Plank Hold (s)': 95,
    'Agility Drill (s)': 15.0,
    'Sit & Reach (cm)': 26,
    'Push-ups (1 min) (reps)': 40,
    'Pull-ups (reps)': 9,
  },
  '5': { // Carlos Pereira
    'Sprint': 14.2,
    'Vertical Jump': 45,
    'Horizontal Jump': 165,
    'Bench Press (kg)': 70,
    'Squat Max (kg)': 110,
    'Deadlift (kg)': 125,
    'VO2 Max (ml/kg/min)': 46,
    'Cooper Test (m)': 2300,
    'Plank Hold (s)': 120,
    'Agility Drill (s)': 17.5,
    'Sit & Reach (cm)': 15,
    'Push-ups (1 min) (reps)': 35,
    'Pull-ups (reps)': 7,
  },
  '6': { // Sofia Rodrigues
    'Sprint': 12.8,
    'Vertical Jump': 50,
    'Horizontal Jump': 178,
    'Bench Press (kg)': 52,
    'Squat Max (kg)': 88,
    'Deadlift (kg)': 100,
    'VO2 Max (ml/kg/min)': 53,
    'Cooper Test (m)': 2650,
    'Plank Hold (s)': 88,
    'Agility Drill (s)': 15.5,
    'Sit & Reach (cm)': 30,
    'Push-ups (1 min) (reps)': 39,
    'Pull-ups (reps)': 8,
  },
  '7': { // Miguel Ferreira
    'Sprint': 13.5,
    'Vertical Jump': 46,
    'Horizontal Jump': 170,
    'Bench Press (kg)': 85,
    'Squat Max (kg)': 135,
    'Deadlift (kg)': 155,
    'VO2 Max (ml/kg/min)': 47,
    'Cooper Test (m)': 2350,
    'Plank Hold (s)': 105,
    'Agility Drill (s)': 16.8,
    'Sit & Reach (cm)': 16,
    'Push-ups (1 min) (reps)': 48,
    'Pull-ups (reps)': 12,
  },
  '8': { // Beatriz Almeida
    'Sprint': 11.3,
    'Vertical Jump': 58,
    'Horizontal Jump': 195,
    'Bench Press (kg)': 58,
    'Squat Max (kg)': 92,
    'Deadlift (kg)': 108,
    'VO2 Max (ml/kg/min)': 55,
    'Cooper Test (m)': 2750,
    'Plank Hold (s)': 92,
    'Agility Drill (s)': 14.2,
    'Sit & Reach (cm)': 25,
    'Push-ups (1 min) (reps)': 43,
    'Pull-ups (reps)': 10,
  },
};

// Mock test results - generated programmatically
export const initialTestResults: TestResult[] = [];

let resultId = 1;

// Generate test results for each athlete
athletes.forEach((athlete, athleteIdx) => {
  const baseValues = athleteBaseValues[athlete.id];

  // For each test type, generate results for dates
  testTypes.forEach((testType, testIdx) => {
    const baseValue = baseValues[testType];

    // Generate results for most dates (80-90% coverage for realism)
    pastDates.forEach((date, dateIdx) => {
      const seed = athleteIdx * 1000 + testIdx * 100 + dateIdx;
      const includeDate = (seed % 100) < 85; // 85% probability

      if (includeDate) {
        initialTestResults.push({
          id: `r${resultId}`,
          athleteId: athlete.id,
          testType: testType,
          date: date,
          value: generateTestValue(testType, baseValue, 0.08, resultId),
        });
        resultId++;
      }
    });
  });
});
